import { gql } from "@apollo/client";
import { graphQlClient } from "lib/graphql-client.js";
import { ArticleJsonLd } from "next-seo";
import dynamic from "next/dynamic";
import { useRef } from "react";

import { site } from "config";
import { fetchLayoutDataServer } from "lib/api/strapi-layout";
import { fetchLatestStories, fetchSingleStory } from "lib/api/strapi-stories";
import { generateBlazeSlides } from "utils/sliders";

import MainSlider from "components/Slider/BlazeSlider/BlazeSlider";
import Layout from "../../components/Layout";
const RecommendedStories = dynamic(
  () => import("@/components/BlogPage/RecommendedStories")
);
const RelatedExperiences = dynamic(
  () =>
    import("components/ExperiencePresentation/RelatedExperiences/RelatedExperiences")
);
const ContentZone = dynamic(
  () => import("../../components/BlogPage/ContentZone"),
  {
    ssr: false,
  }
);

const SocialShare = dynamic(
  () => import("@/components/links/SocialLinks/SocialShare")
);
const AffiliateDisclaimer = dynamic(
  () => import("components/BlogPage/Single/AffiliateDisclaimer")
);
const CategoryTags = dynamic(
  () => import("components/BlogPage/Single/CategoryTags")
);

import AuthorInfo from "@/components/BlogPage/Single/AuthorInfo";
import PostMeta from "@/components/BlogPage/Single/PostMeta";
import styles from "./Story.module.scss";

const BlogDetail = ({ blog, blogs, layoutData }) => {
  const relatedExperiencesRef = useRef(null);
  const relatedStoriesRef = useRef(null);

  const URL = site + "/stories/" + blog?.slug;

  const SEO = {
    ...blog?.seo,
    canonicalURL: URL,
  };

  const slides = generateBlazeSlides({
    slides: blog?.HeroSlider,
    type: "blogMinimal",
    ctaButton: false,
  });

  const authorName = blog?.author.authorName;
  return (
    <Layout seo={SEO} layoutData={layoutData}>
      <ArticleJsonLd
        url={`${site}/stories/${blog?.slug}`}
        title={blog?.title}
        images={blog?.HeroSlider?.map((slide) => slide?.sliderImageLarge?.url)}
        datePublished={blog?.publishedAt}
        dateModified={blog?.updatedAt}
        authorName={[
          {
            name: authorName,
            // url: "https://example.com",
          },
        ]}
        publisherName="Rob Holliday"
        publisherLogo="https://res.cloudinary.com/dail4fbms/image/upload/v1675179842/wherenext_logo_outline_1c44641c22.png"
        description={blog?.description}
        isAccessibleForFree={true}
      />

      <div className={`hero-container`}>
        <MainSlider sideNav={true}>{slides}</MainSlider>
      </div>

      <>
        <div
          className={`${styles.blogPost}`}
          id="mainContent"
          style={{ scrollMarginTop: "25vh" }}
        >
          <div className="variable-container">
            <div className={`${styles.content}`}>
              <PostMeta post={blog} />
              <div className={`${styles.postContent}`}>
                <div className={`${styles.socialShare}`}>
                  <SocialShare blog={blog} url={URL} direction="vertical" />
                </div>

                <div style={{ maxWidth: "724px", margin: "auto" }}>
                  {<ContentZone contents={blog?.ContentZone} />}
                </div>
              </div>
              <CategoryTags categories={blog?.story_category} />

              <div
                className={`${styles.socialShare} ${styles.socialShareMobile}`}
              >
                <SocialShare blog={blog} url={URL} direction="horizontal" />
              </div>

              {blog?.affiliate_disclaimer && <AffiliateDisclaimer />}

              <div className={`${styles.divider}`} />

              <AuthorInfo post={blog} />
            </div>
          </div>
        </div>
        <div className={`${styles.relatedContent} variable-container`}>
          {!!blog?.related_experiences?.length && (
            <div className={`${styles.relatedContentWrapper}`}>
              <RelatedExperiences
                experiences={blog?.related_experiences}
                title="Related Experiences"
                sideScroll="all"
                sideScrollButtons={true}
                cardRowRef={relatedExperiencesRef}
              />
            </div>
          )}
          {blogs && (
            <div className={`${styles.relatedContentWrapper}`}>
              <RecommendedStories
                blogs={blogs}
                cardRowRef={relatedStoriesRef}
              />
            </div>
          )}
        </div>
      </>
    </Layout>
  );
};

export default BlogDetail;

export async function getStaticProps(context) {
  const story = await fetchSingleStory(context.params.slug, {
    process: true,
    type: "full",
  });

  const latestStories = await fetchLatestStories({
    start: 0,
    limit: 3,
    exclude: story?.id,
  });

  // const layoutData = await fetchLayoutData();
  const layoutData = await fetchLayoutDataServer();

  return {
    props: {
      blog: story,
      blogs: latestStories,
      layoutData,
    },
  };
}

export async function getStaticPaths() {
  const strapiApolloClient = graphQlClient();

  let storyPathsResponse = await strapiApolloClient.query({
    query: gql`
      query RelatedStoriesQuery {
        articles(
          pagination: { limit: 1000 }
          filters: { publishedAt: { notNull: true } }
        ) {
          data {
            id
            attributes {
              slug
            }
          }
        }
      }
    `,
  });

  const paths = storyPathsResponse.data.articles.map((path) => {
    return {
      params: { slug: path.data.attributes.slug.toString() },
    };
  });

  return {
    paths: paths,
    fallback: false,
  };
}
